import React, { useEffect, useCallback, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { lockBodyScroll, unlockBodyScroll } from "../../lib/bodyScrollLock";
import { isTopOverlay, registerOverlay, unregisterOverlay } from "../../lib/overlayStack";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export interface DrawerProps {
  open: boolean;
  onClose?: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: number;
  className?: string;
  /** Klasse auf der Dialogwurzel; seit dem Portal in `document.body` greifen
   *  Nachfahren-Selektoren der Aufrufstelle nicht mehr. */
  rootClassName?: string;
  closeOnBackdrop?: boolean;
  closeLabel?: string;
  /// S54: Inhalt bringt einen EIGENEN Kopf mit (z. B. OrderDetailPanel) —
  /// unterdrückt den Drawer-Kopf, Escape/Backdrop-Schließen bleibt.
  hideHeader?: boolean;
  /// Fachlicher Dialogname für Screenreader (Fallback: title bzw. "Drawer")
  ariaLabel?: string;
  /// Der Inhalt bleibt scrollbar, die native Scrollleiste wird aber visuell ausgeblendet.
  hideBodyScrollbar?: boolean;
}

/**
 * Legacy modal side flow. For Max's "Drawer" / sliding panel requests,
 * use DetailWorkspace + WorkbenchDetail instead (see CLAUDE.md).
 * Use this modal variant for a new request only when Max explicitly asks
 * for a different presentation than the embedded order/article detail rail.
 *
 * Mirrors <Modal>'s UX (escape-to-close, focus trap, body scroll lock,
 * backdrop click) but slides in from the right instead of centering.
 * Intended for multi-step side flows (e.g. Batch-Dispatch) where the
 * main page stays contextually visible behind.
 */
export default function Drawer({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  width = 420,
  className = "",
  rootClassName = "",
  closeOnBackdrop = true,
  closeLabel = "Close",
  hideHeader = false,
  ariaLabel,
  hideBodyScrollbar = false,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const overlayIdRef = useRef<number | null>(null);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape" && isTopOverlay(overlayIdRef.current)) {
      e.preventDefault();
      e.stopPropagation();
      onCloseRef.current?.();
      return;
    }
    if (e.key === "Tab" && panelRef.current && isTopOverlay(overlayIdRef.current)) {
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  useEffect(() => {
    if (panelRef.current) {
      if (open) panelRef.current.removeAttribute("inert");
      else panelRef.current.setAttribute("inert", "");
    }
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      overlayIdRef.current = registerOverlay(110);
      document.addEventListener("keydown", handleKeyDown);
      lockBodyScroll();
      requestAnimationFrame(() => {
        const focusable = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
        if (focusable?.length) focusable[0].focus();
      });
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        unregisterOverlay(overlayIdRef.current);
        overlayIdRef.current = null;
        unlockBodyScroll();
        previousFocusRef.current?.focus();
      };
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, handleKeyDown]);

  if (typeof document === "undefined") return null;
  // Escape transformed workbench ancestors: every drawer belongs to the viewport.
  return createPortal(
    <div
      className={`fixed inset-0 z-[110] ${open ? "pointer-events-auto" : "pointer-events-none"} ${rootClassName}`.trim()}
      role="dialog"
      aria-modal="true"
      aria-hidden={!open}
      aria-label={ariaLabel || (typeof title === "string" ? title : "Drawer")}
    >
      {/* Backdrop */}
      <button
        type="button"
        tabIndex={-1}
        aria-label={closeLabel}
        className={`absolute inset-0 bg-background/70 backdrop-blur-sm transition-opacity duration-200 motion-reduce:transition-none ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={closeOnBackdrop ? onClose : undefined}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`absolute right-0 top-0 h-full bg-surface-container-low border-l border-outline-variant/15 transition-transform duration-300 ease-out motion-reduce:transition-none flex flex-col ${
          open
            ? "translate-x-0 shadow-[-20px_0_60px_rgba(6,14,32,0.6)]"
            : "translate-x-full shadow-none"
        } ${className}`}
        style={{ width: `min(100vw, ${width}px)` }}
      >
        {/* Header */}
        {!hideHeader && (title || subtitle || onClose) && (
          <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-outline-variant/10 shrink-0">
            <div className="flex items-start gap-3 min-w-0">
              {icon && (
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20 shrink-0">
                  {icon}
                </div>
              )}
              <div className="min-w-0">
                {title && (
                  <h2 className="text-balance text-base font-bold font-headline text-on-surface tracking-tight">
                    {title}
                  </h2>
                )}
                {subtitle && <p className="text-xs text-on-surface-variant mt-0.5">{subtitle}</p>}
              </div>
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1.5 text-on-surface-variant hover:text-on-surface hover:bg-outline-variant/15 transition-colors motion-reduce:transition-none shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                aria-label={closeLabel}
              >
                <span className="material-symbols-outlined text-base" aria-hidden="true">
                  close
                </span>
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div
          className={`flex-1 overflow-y-auto overscroll-contain ${
            hideBodyScrollbar ? "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden" : ""
          }`}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="safe-bottom px-5 py-3 border-t border-outline-variant/10 bg-surface-container shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
